import { memo } from "preact/compat";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { MessageRowProps, RowHandlers } from "../../sdk/theme";
import { cleanSnippet } from "../../shared/quotes";
import {
  CATEGORIES,
  type Category,
  type MessageList as ListData,
  type MessageSummary,
} from "../../shared/types";
import {
  addContact,
  addLabel,
  assignMenu,
  blockSender,
  deleteMessages,
  emptyFolder,
  laterOptions,
  markFlagged,
  markMuted,
  markPinned,
  markSeen,
  moveToRole,
  moveToTab,
  notSpam,
  pickDateTime,
  removeLabel,
  ruleFromSender,
  setVip,
  snooze,
  TAB_NAMES,
  type Target,
} from "../actions";
import { api } from "../api";
import { saveSearch } from "../components/AdvancedSearch";
import { Avatar, AvatarLookup } from "../components/Avatar";
import { openContextMenu } from "../components/ContextMenu";
import { Icon } from "../components/Icon";
import { PaneDivider } from "../components/PaneDivider";
import { Reader } from "../components/Reader";
import { SearchChips } from "../components/SearchChips";
import { moveTo } from "../components/Sidebar";
import { useSwipe } from "../components/Swipe";
import { Empty, IconButton, Menu, type MenuItem, Spinner } from "../components/ui";
import { displayName, formatListDate } from "../format";
import { useLayout } from "../layout";
import { keepTotal, live, lostMailbox, publishPolled, watchList } from "../live";
import { useListBadges } from "../modules";
import { overridable } from "../overrides";
import { freshness, keepLoaded, pagePath, refetchForPage } from "../paging";
import { getCurrentMailbox, type MailSource, mailHash, navigate, sourcePath } from "../router";
import {
  app,
  errorToast,
  openComposer,
  refreshFolders,
  refreshSearches,
  replyTo,
  together,
  useFields,
  useStore,
} from "../store";

const TAB_ICONS = { primary: "inbox", updates: "info", promotions: "tag" } as const;
/** GET /inbox/tabs, which live updates also carry for an Inbox tab's list. */
type TabCounts = { unread: Record<Category, number> };

const TAB_KEY = "cloudmail.inboxTab";
const savedTab = (): Category => {
  try {
    return CATEGORIES.find((entry) => entry === sessionStorage.getItem(TAB_KEY)) ?? "primary";
  } catch {
    return "primary";
  }
};

const sourceQuery = (source: MailSource, conversation: boolean, tab: Category | null = null) => {
  const params = new URLSearchParams();
  if (source.kind === "folder") params.set("folder", source.id);
  if (source.kind === "label") params.set("label", source.id);
  if (source.kind === "view") {
    if (source.id === "mine" || source.id === "unassigned") {
      params.set("view", "all");
      params.set("assignee", source.id === "mine" ? "me" : "none");
    } else params.set("view", source.id);
  }
  if (source.kind === "search") params.set("q", source.query);
  if (conversation) params.set("conversation", "1");
  if (tab) params.set("tab", tab);
  return params;
};

export const MailPage = ({ source, open }: { source: MailSource; open: string | null }) => {
  const {
    me,
    folders,
    labels,
    version,
    mailbox,
    mailboxes,
    searches: savedSearches,
  } = useFields(app, "me", "folders", "labels", "version", "mailbox", "mailboxes", "searches");
  const activeBox = mailbox ? mailboxes.find((entry) => entry.id === mailbox) : undefined;
  // The unified inbox lists every mailbox's Inbox; each row knows which mailbox it is in.
  const unified = source.kind === "view" && source.id === "inboxes";
  const canAssign = !unified && activeBox !== undefined && activeBox.role !== "read";
  const settings = me?.settings;
  const layout = useLayout();
  const folder =
    source.kind === "folder" ? folders.find((entry) => entry.id === source.id) : undefined;
  const role = folder?.role ?? null;
  const junkFolders = useMemo(
    () => new Set(folders.filter((entry) => entry.role === "junk").map((entry) => entry.id)),
    [folders],
  );
  // Drafts and Sent read better as single messages; scheduled sends are always single.
  const conversation =
    (settings?.conversationView ?? true) &&
    role !== "drafts" &&
    !(source.kind === "view" && source.id === "scheduled");
  const [data, setData] = useState<ListData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focus, setFocus] = useState(0);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  // The Inbox is split into tabs while the inbox tabs module is on, unless the person turned
  // them off.
  const tabsOn =
    role === "inbox" && (settings?.inboxTabs ?? true) && (me?.modules ?? []).includes("inbox-tabs");
  const [chosenTab, setChosenTab] = useState<Category>(savedTab);
  const tab = tabsOn ? chosenTab : null;
  const chooseTab = (next: Category) => {
    setChosenTab(next);
    try {
      sessionStorage.setItem(TAB_KEY, next);
    } catch {
      // Private windows may refuse storage; the tab then resets on reload.
    }
    if (open) navigate(sourcePath(source));
  };
  const [tabUnread, setTabUnread] = useState<Record<Category, number> | null>(null);
  const sourceKey = `${JSON.stringify(source)}|${tab ?? ""}`;
  const pageSize = settings?.pageSize ?? 50;

  const listPath = useMemo(() => {
    const params = sourceQuery(source, conversation, tab);
    params.set("limit", String(pageSize));
    return `/messages?${params}`;
  }, [sourceKey, conversation, pageSize]);

  /** The list on screen, for what reads it outside a render. */
  const shown = useRef<ListData | null>(null);
  /** The path of the list on screen now, for a page that comes back after the person moved on. */
  const listNow = useRef(listPath);
  listNow.current = listPath;
  /** How many rows "Load more" added past the first page; a refresh keeps that many. */
  const more = useRef(0);
  /** Counts refreshes, so one that finishes late doesn't replace a newer one. */
  const refreshes = useRef(0);
  /** Refreshes on their way. */
  const pending = useRef(0);
  /** When the list was last fetched or pushed. */
  const fetched = useMemo(() => freshness(), []);

  const show = useCallback((list: ListData) => {
    // A live update without a total keeps the one on screen.
    const next = keepTotal(list, shown.current);
    shown.current = next;
    setData(next);
    setSelected(
      (current) => new Set([...current].filter((id) => list.messages.some((m) => m.id === id))),
    );
  }, []);

  /** Shows a fresh first page with the rows loaded past it, fetched again. */
  const refresh = useCallback(
    async (first: ListData, signal?: AbortSignal) => {
      refreshes.current += 1;
      const ticket = refreshes.current;
      pending.current += 1;
      try {
        const list = await keepLoaded(
          first,
          more.current,
          (cursor, limit) => api.get<ListData>(pagePath(listPath, cursor, limit), signal),
          shown.current,
        );
        if (ticket === refreshes.current) show(list);
      } finally {
        pending.current -= 1;
      }
    },
    [listPath],
  );

  const load = useCallback(
    async (signal?: AbortSignal, polled = false) => {
      const done = fetched.start();
      try {
        const list = await api.get<ListData>(listPath, signal);
        // While the live connection is down, a polled list stands in for a pushed one, so the
        // open conversation still picks up new mail from it.
        if (polled) publishPolled(listPath, list);
        await refresh(list, signal);
      } catch (error) {
        if (!(error instanceof DOMException)) errorToast(error);
      } finally {
        done();
        setLoading(false);
      }
    },
    [listPath, refresh],
  );

  // Unread counts on the Updates and Promotions tabs.
  const loadTabs = useCallback((signal?: AbortSignal) => {
    api
      .get<TabCounts>("/inbox/tabs", signal)
      .then((result) => setTabUnread(result.unread))
      .catch(() => {});
  }, []);

  // The live connection sends this list whenever it changes, with the tabs' counts for an Inbox
  // tab, so neither needs fetching.
  useEffect(() => {
    watchList(listPath, tabsOn);
    return () => watchList(null);
  }, [listPath, tabsOn]);
  const pushed = useStore(live, (state) => state.list);
  // Only lists the server pushed: a polled one is on screen already.
  const pushedAt = pushed && !pushed.polled ? pushed.at : 0;
  useEffect(() => {
    if (!pushedAt || pushed?.path !== listPath) return;
    void refresh(pushed.data as ListData).catch(() => {});
    if (!tabsOn) return;
    if (pushed.tabs) setTabUnread((pushed.tabs as TabCounts).unread);
    else loadTabs();
  }, [pushedAt]);

  useEffect(() => {
    if (!tabsOn) return;
    const controller = new AbortController();
    loadTabs(controller.signal);
    return () => controller.abort();
  }, [tabsOn, version, listPath]);

  // Another list (or the same one in pages of another size) starts from one page again, and a
  // refresh of the one before that is still on its way is dropped.
  useEffect(() => {
    more.current = 0;
    refreshes.current += 1;
  }, [listPath]);

  useEffect(() => {
    setLoading(true);
    shown.current = null;
    setData(null);
    setSelected(new Set());
    setFocus(0);
    setKeyboardFocus(false);
  }, [sourceKey]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, version]);

  // New mail normally arrives over the live connection; polling is the fallback for when it's
  // down, and a rare safety net when it's up.
  const connected = useStore(live, (state) => state.connected);
  useEffect(() => {
    const timer = setInterval(
      () => {
        if (document.visibilityState === "visible") {
          void load(undefined, true);
          // A static import: a lazily loaded chunk isn't there to load when offline.
          void refreshFolders().catch((error: unknown) => {
            // Removed from this shared mailbox (or it was suspended): back to your own mail,
            // which also ends the live connection's retries.
            if (lostMailbox(error, getCurrentMailbox())) navigate("#/", true);
          });
          // Searches saved on another device.
          void refreshSearches();
        }
      },
      connected ? 300_000 : 30_000,
    );
    return () => clearInterval(timer);
  }, [load, connected]);

  // The unified inbox also refreshes when the unread count of any mailbox moves, unless the
  // change that moved it brought the list already: a change here or a poll fetches it, and a
  // live update carries it along with the counts.
  const unreadAcross = mailboxes.map((box) => box.unread).join(",");
  const seenUnread = useRef(unreadAcross);
  useEffect(() => {
    if (seenUnread.current === unreadAcross) return;
    seenUnread.current = unreadAcross;
    if (!unified) return;
    const pushedList = live.get().list;
    if (pushedList?.path === listPath) fetched.seen(pushedList.at);
    if (!fetched.fresh()) void load();
  }, [unreadAcross, unified]);

  const loadMore = async () => {
    if (!data?.nextCursor || loadingMore) return;
    setLoadingMore(true);
    const asked = refreshes.current;
    try {
      const page = await api.get<ListData>(pagePath(listPath, data.nextCursor, pageSize));
      // Another list is on screen now: the page isn't part of it.
      if (listNow.current !== listPath) return;
      more.current += page.messages.length;
      // A refresh may have landed meanwhile: add to what is on screen now.
      const current = shown.current ?? data;
      const ids = new Set(current.messages.map((message) => message.id));
      show({
        ...page,
        // Only the first page counts the total.
        total: current.total,
        messages: [...current.messages, ...page.messages.filter((m) => !ids.has(m.id))],
      });
      // A refresh still on its way counted fewer rows, and one since went on from a fresher
      // cursor than this page: either gives way to one that keeps these rows.
      if (refetchForPage(asked, refreshes.current, pending.current)) {
        refreshes.current += 1;
        void load();
      }
    } catch (error) {
      errorToast(error);
    } finally {
      setLoadingMore(false);
    }
  };

  const messages = data?.messages ?? [];
  const openKey = (message: MessageSummary) => (conversation ? message.threadId : message.id);
  const selectedIds = selected.size > 0 ? [...selected] : [];
  const focused = messages[focus];
  const openMessage = messages.find((message) => openKey(message) === open);

  const targetFor = (ids: string[]): Target => ({
    ids,
    threads: conversation,
    scopeFolderId: source.kind === "folder" ? source.id : null,
  });
  /** null for the person's own mailbox, else the shared mailbox a unified-inbox row is in. */
  const boxOf = (message: MessageSummary) =>
    message.mailboxId && message.mailboxId !== me?.id ? message.mailboxId : null;
  const mailboxLabel = (message: MessageSummary) => {
    const box = boxOf(message);
    const entry = box ? mailboxes.find((candidate) => candidate.id === box) : undefined;
    return entry ? entry.name || entry.address : null;
  };
  const messagePath = (message: MessageSummary) =>
    unified
      ? mailHash(`#/view/inboxes/${encodeURIComponent(openKey(message))}`, boxOf(message))
      : `${sourcePath(source)}/${encodeURIComponent(openKey(message))}`;
  /** Targets for acting on messages; the unified inbox needs one per mailbox. */
  const targetsFor = (ids: string[]): Target[] => {
    if (!unified) return [targetFor(ids)];
    const groups = new Map<string, { mailbox: string | null; folderId: string; ids: string[] }>();
    for (const message of messages.filter((entry) => ids.includes(entry.id))) {
      const mailbox = boxOf(message);
      const key = mailbox ?? "";
      const group = groups.get(key) ?? { mailbox, folderId: message.folderId, ids: [] };
      group.ids.push(message.id);
      groups.set(key, group);
    }
    return [...groups.values()].map((group) => ({
      ids: group.ids,
      threads: conversation,
      scopeFolderId: group.folderId,
      mailbox: group.mailbox,
    }));
  };
  /** Acts on each mailbox's share of the messages; the list and folders then reload once. */
  const forEachTarget = (ids: string[], act: (target: Target) => Promise<unknown>) =>
    void together(targetsFor(ids).map((target) => () => act(target)));
  const actionIds = () =>
    selectedIds.length > 0
      ? selectedIds
      : openMessage
        ? [openMessage.id]
        : focused
          ? [focused.id]
          : [];

  const afterRemoval = (ids: string[]) => {
    setSelected(new Set());
    if (openMessage && ids.includes(openMessage.id)) {
      const index = messages.indexOf(openMessage);
      const next = messages[index + 1] ?? messages[index - 1];
      navigate(
        next && !ids.includes(next.id) && layout.readingPane !== "off"
          ? messagePath(next)
          : sourcePath(source),
      );
    }
  };

  const select = (message: MessageSummary) => {
    if (message.isDraft && message.sendAt === null) {
      openComposer({ mode: "edit", sourceId: message.id });
      return;
    }
    navigate(messagePath(message));
  };

  const run = {
    archive: (ids = actionIds()) => {
      if (ids.length === 0) return;
      forEachTarget(ids, (target) =>
        moveToRole(target, "archive", unified ? target.scopeFolderId : (folder?.id ?? null)),
      );
      afterRemoval(ids);
    },
    remove: (ids = actionIds()) => {
      if (ids.length === 0) return;
      forEachTarget(ids, (target) =>
        deleteMessages(target, unified ? target.scopeFolderId : (folder?.id ?? null)),
      );
      afterRemoval(ids);
    },
    spam: (ids = actionIds()) => {
      if (ids.length === 0 || unified) return;
      if (role === "junk") void notSpam(targetFor(ids));
      else void moveToRole(targetFor(ids), "junk", folder?.id ?? null);
      afterRemoval(ids);
    },
    toggleRead: (seen?: boolean, ids = actionIds()) => {
      if (ids.length === 0) return;
      const first = messages.find((message) => message.id === ids[0]);
      forEachTarget(ids, (target) => markSeen(target, seen ?? !first?.seen));
    },
    toggleFlag: (ids = actionIds()) => {
      if (ids.length === 0) return;
      const first = messages.find((message) => message.id === ids[0]);
      forEachTarget(ids, (target) => markFlagged(target, !first?.flagged));
    },
    togglePin: (ids = actionIds()) => {
      if (ids.length === 0) return;
      const chosen = messages.filter((message) => ids.includes(message.id));
      const pinned = !chosen.every((message) => message.pinned);
      forEachTarget(ids, (target) => markPinned(target, pinned));
    },
    toggleMute: (ids = actionIds()) => {
      if (ids.length === 0) return;
      const chosen = messages.filter((message) => ids.includes(message.id));
      const mute = !chosen.every((message) => message.muted);
      forEachTarget(ids, (target) => markMuted(target, mute));
      // Muting archives the conversation, so it leaves the Inbox.
      if (mute && (role === "inbox" || unified)) afterRemoval(ids);
    },
  };

  // Keyboard shortcuts, Gmail-style.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest("input, textarea, select, [contenteditable=true], .composer")) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const move = (delta: number) => {
        const next = Math.max(0, Math.min(messages.length - 1, focus + delta));
        setFocus(next);
        setKeyboardFocus(true);
        const message = messages[next];
        if (open && message) select(message);
        listRef.current
          ?.querySelectorAll(".message-row")
          [next]?.scrollIntoView({ block: "nearest" });
      };
      switch (event.key) {
        case "j":
        case "ArrowDown":
          move(1);
          break;
        case "k":
        case "ArrowUp":
          move(-1);
          break;
        case "o":
        case "Enter":
          if (focused) select(focused);
          break;
        case "u":
        case "Escape":
          if (open) navigate(sourcePath(source));
          else setSelected(new Set());
          break;
        case "x":
          if (focused) {
            setSelected((current) => {
              const next = new Set(current);
              if (next.has(focused.id)) next.delete(focused.id);
              else next.add(focused.id);
              return next;
            });
          }
          break;
        case "s":
          run.toggleFlag();
          break;
        case "m":
          run.toggleMute();
          break;
        case "e":
          run.archive();
          break;
        case "#":
        case "Delete":
          run.remove();
          break;
        case "!":
          run.spam();
          break;
        case "I":
          run.toggleRead(true);
          break;
        case "U":
          run.toggleRead(false);
          break;
        case "c":
          openComposer({ mode: "new", sourceId: null });
          break;
        case "r":
        case "a":
        case "f": {
          const message = openMessage ?? focused;
          if (message) {
            const mode = event.key === "r" ? "reply" : event.key === "a" ? "reply-all" : "forward";
            replyTo(mode, message);
          }
          break;
        }
        case "/":
          document.querySelector<HTMLInputElement>(".search input")?.focus();
          break;
        default:
          return;
      }
      event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const title =
    source.kind === "folder"
      ? (folder?.name ?? "Folder")
      : source.kind === "label"
        ? (labels.find((label) => label.id === source.id)?.name ?? "Label")
        : source.kind === "search"
          ? `Search: ${source.query}`
          : {
              flagged: "Flagged",
              unread: "Unread",
              scheduled: "Scheduled",
              snoozed: "Snoozed",
              vip: "VIP",
              all: "All mail",
              mine: "Assigned to me",
              unassigned: "Unassigned",
              inboxes: "All inboxes",
            }[source.id];

  const allSelected = messages.length > 0 && messages.every((message) => selected.has(message.id));
  const pane = layout.readingPane;
  const showList = pane !== "off" || !open;
  const showReader = open !== null;
  const labelMenu = (ids = actionIds()): MenuItem[] => {
    const current = new Set(messages.filter((m) => ids.includes(m.id)).flatMap((m) => m.labelIds));
    return labels.length === 0
      ? [{ heading: "Create labels in the sidebar" }]
      : labels.map((label) => ({
          label: label.name,
          color: label.color,
          checked: current.has(label.id),
          onSelect: () =>
            current.has(label.id)
              ? removeLabel(targetFor(ids), label.id)
              : addLabel(targetFor(ids), label.id),
        }));
  };
  const moveMenu = (chosen?: string[]): MenuItem[] =>
    folders
      .filter((entry) => entry.id !== folder?.id && entry.role !== "drafts")
      .map((entry) => ({
        label: entry.name,
        icon: "folder" as const,
        onSelect: () => {
          const ids = chosen ?? actionIds();
          void moveTo(ids, entry.id, conversation, source.kind === "folder" ? source.id : null);
          afterRemoval(ids);
        },
      }));
  // Moving to another tab takes the messages off this one, and the sender's mail follows.
  const tabMenu = (chosen?: string[]): MenuItem[] => {
    const ids = chosen ?? actionIds();
    const senders = new Set(
      messages.filter((m) => ids.includes(m.id)).map((m) => m.from.address.toLowerCase()),
    );
    return [
      { heading: "Mail from this sender goes there from now on" },
      ...CATEGORIES.filter((entry) => entry !== tab).map((entry) => ({
        label: TAB_NAMES[entry],
        icon: TAB_ICONS[entry],
        onSelect: () => {
          void moveToTab(targetFor(ids), entry, senders.size === 1 ? [...senders][0] : undefined);
          afterRemoval(ids);
        },
      })),
    ];
  };
  const snoozeMenu = (chosen?: string[]): MenuItem[] => [
    ...laterOptions().map((option) => ({
      label: option.label,
      icon: "alarm" as const,
      onSelect: () => {
        const ids = chosen ?? actionIds();
        void snooze(targetFor(ids), option.at);
        afterRemoval(ids);
      },
    })),
    {
      label: "Pick date and time…",
      icon: "calendar",
      onSelect: () => {
        const at = pickDateTime("Snooze until");
        if (at === null) return;
        const ids = chosen ?? actionIds();
        void snooze(targetFor(ids), at);
        afterRemoval(ids);
      },
    },
  ];

  const hasTarget = selectedIds.length > 0 || openMessage !== undefined;

  // Right-click on a message acts on the selection when the message is part of it, else on
  // that message alone.
  const rowMenu = (message: MessageSummary): MenuItem[] => {
    const ids = selected.has(message.id) ? [...selected] : [message.id];
    const chosen = messages.filter((entry) => ids.includes(entry.id));
    const single = ids.length === 1;
    const allSeen = chosen.every((entry) => entry.seen);
    const allFlagged = chosen.every((entry) => entry.flagged);
    const allPinned = chosen.every((entry) => entry.pinned);
    const allMuted = chosen.every((entry) => entry.muted);
    const senders = chosen.filter((entry) => !entry.isDraft && entry.from.address !== "");
    const allVip = senders.every((entry) => entry.vip);
    const editable = message.isDraft && message.sendAt === null;
    const incoming = !message.isDraft && role !== "sent";
    const items: MenuItem[] = [];
    if (single) {
      items.push({
        label: editable ? "Edit draft" : "Open",
        icon: editable ? "pencil" : "mailOpen",
        onSelect: () => select(message),
      });
      if (!message.isDraft)
        items.push(
          {
            label: "Reply",
            icon: "reply",
            onSelect: () => replyTo("reply", message),
          },
          {
            label: "Reply all",
            icon: "replyAll",
            onSelect: () => replyTo("reply-all", message),
          },
          {
            label: "Forward",
            icon: "forward",
            onSelect: () => replyTo("forward", message),
          },
        );
      items.push({ separator: true });
    } else {
      items.push({ heading: `${ids.length} selected` });
    }
    if (role !== "archive")
      items.push({ label: "Archive", icon: "archive", onSelect: () => run.archive(ids) });
    items.push(
      {
        label: allSeen ? "Mark as unread" : "Mark as read",
        icon: allSeen ? "mail" : "mailOpen",
        onSelect: () => run.toggleRead(!allSeen, ids),
      },
      {
        label: allFlagged ? "Unflag" : "Flag",
        icon: "star",
        onSelect: () => run.toggleFlag(ids),
      },
      {
        label: allPinned ? "Unpin" : "Pin to top",
        icon: "pin",
        onSelect: () => run.togglePin(ids),
      },
    );
    if (!message.isDraft)
      items.push({
        label: allMuted ? "Unmute" : "Mute conversation",
        icon: "bellOff",
        onSelect: () => run.toggleMute(ids),
      });
    if (tabsOn && incoming)
      items.push({ label: "Move to tab", icon: "inbox", submenu: () => tabMenu(ids) });
    // Folders and labels belong to one mailbox, so the unified inbox leaves them out.
    if (!unified)
      items.push(
        { label: "Move to", icon: "folder", submenu: () => moveMenu(ids) },
        { label: "Labels", icon: "tag", submenu: () => labelMenu(ids) },
      );
    // VIPs are kept per mailbox, so the unified inbox leaves this out too.
    if (incoming && !unified && senders.length > 0) {
      const several = new Set(senders.map((entry) => entry.from.address.toLowerCase())).size > 1;
      const who = several ? "senders" : "sender";
      items.push({
        label: allVip ? `Remove ${who} from VIPs` : `Mark ${who} as VIP`,
        icon: "crown",
        onSelect: () =>
          void setVip(
            senders.map((entry) => entry.from),
            !allVip,
          ),
      });
    }
    if (!message.isDraft && !unified)
      items.push({ label: "Snooze", icon: "alarm", submenu: () => snoozeMenu(ids) });
    if (canAssign)
      items.push({
        label: "Assign",
        icon: "user",
        submenu: () => assignMenu(ids, single ? message.assignee : null),
      });
    if (single && incoming && !unified) {
      items.push(
        { separator: true },
        {
          label: "Make a rule from this…",
          icon: "filter",
          onSelect: () => ruleFromSender(message.from),
        },
        {
          label: "Add sender to contacts",
          icon: "book",
          onSelect: () => void addContact(message.from),
        },
        {
          label: `Block ${message.from.address}`,
          icon: "ban",
          onSelect: async () => {
            if (await blockSender(message.from.address)) run.spam(ids);
          },
        },
      );
    }
    items.push(
      { separator: true },
      {
        label: selected.has(message.id) ? "Deselect" : "Select",
        icon: "check",
        onSelect: () =>
          setSelected((current) => {
            const next = new Set(current);
            if (next.has(message.id)) next.delete(message.id);
            else next.add(message.id);
            return next;
          }),
      },
    );
    if (!message.isDraft && !unified)
      items.push({
        label: role === "junk" ? "Not spam" : "Report spam",
        icon: "junk",
        onSelect: () => run.spam(ids),
      });
    items.push({ label: "Delete", icon: "trash", danger: true, onSelect: () => run.remove(ids) });
    return items;
  };

  // Rows only re-render when what they show changes; their handlers read the latest state
  // when used.
  const rowHandlers = (message: MessageSummary, index: number): RowHandlers => {
    const toggleRead = () => {
      forEachTarget([message.id], (target) => markSeen(target, !message.seen, { undo: true }));
    };
    return {
      onCheck: (checked) =>
        setSelected((current) => {
          const next = new Set(current);
          if (checked) next.add(message.id);
          else next.delete(message.id);
          return next;
        }),
      onOpen: () => {
        setFocus(index);
        select(message);
      },
      onFlag: () => run.toggleFlag([message.id]),
      archive: () => run.archive([message.id]),
      remove: () => run.remove([message.id]),
      toggleRead,
      snooze: (event) => openContextMenu(event, snoozeMenu([message.id])),
      onContextMenu: (event) => {
        setFocus(index);
        openContextMenu(event, rowMenu(message));
      },
      dragIds: () => (selected.has(message.id) ? [...selected] : [message.id]),
    };
  };
  const latestRowHandlers = useRef(rowHandlers);
  latestRowHandlers.current = rowHandlers;
  const handlersFor = useCallback(
    (message: MessageSummary, index: number) => latestRowHandlers.current(message, index),
    [],
  );

  return (
    <div class={`mail-page pane-${pane}${open ? " has-open" : ""}`} data-part="mail-page">
      {showList && (
        <section class="list-pane" data-part="list-pane" aria-label={title}>
          <ListToolbar
            title={selected.size > 0 ? `${selected.size} selected` : title}
            total={data ? data.total : null}
            selected={selected.size}
            allSelected={allSelected}
            onToggleAll={() =>
              setSelected(allSelected ? new Set() : new Set(messages.map((message) => message.id)))
            }
          >
            {hasTarget ? (
              <>
                {role !== "archive" && (
                  <IconButton icon="archive" label="Archive (e)" onClick={() => run.archive()} />
                )}
                <IconButton icon="trash" label="Delete (#)" onClick={() => run.remove()} />
                <Menu
                  align="right"
                  submenuNavigation
                  trigger={({ toggle }) => (
                    <IconButton
                      icon="more"
                      label="More selected message actions"
                      onClick={toggle}
                    />
                  )}
                  items={() => {
                    const items: MenuItem[] = [
                      {
                        label: "Mark read / unread",
                        icon: "mailOpen",
                        onSelect: () => run.toggleRead(),
                      },
                      { label: "Flag / unflag", icon: "star", onSelect: () => run.toggleFlag() },
                      { label: "Pin / unpin", icon: "pin", onSelect: () => run.togglePin() },
                      { label: "Mute / unmute", icon: "bellOff", onSelect: () => run.toggleMute() },
                    ];
                    if (!unified)
                      items.push(
                        {
                          label: role === "junk" ? "Not spam" : "Report spam",
                          icon: role === "junk" ? "inbox" : "junk",
                          onSelect: () => run.spam(),
                        },
                        { label: "Move to", icon: "folder", submenu: moveMenu() },
                        { label: "Labels", icon: "tag", submenu: labelMenu() },
                        { label: "Snooze", icon: "alarm", submenu: snoozeMenu() },
                      );
                    if (canAssign)
                      items.push({
                        label: "Assign",
                        icon: "user",
                        submenu: assignMenu(actionIds()),
                      });
                    return items;
                  }}
                />
              </>
            ) : (
              <>
                <IconButton icon="refresh" label="Refresh" onClick={() => void load()} />
                {source.kind === "search" &&
                  !savedSearches.some((entry) => entry.query === source.query) && (
                    <button
                      type="button"
                      class="ghost small"
                      onClick={() => void saveSearch(source.query)}
                    >
                      Save search
                    </button>
                  )}
                {(role === "trash" || role === "junk") && messages.length > 0 && (
                  <button
                    type="button"
                    class="ghost small"
                    onClick={() => folder && void emptyFolder(folder)}
                  >
                    Empty {folder?.name}
                  </button>
                )}
              </>
            )}
          </ListToolbar>
          {source.kind === "search" && <SearchChips query={source.query} />}
          {tabsOn && (
            <div class="inbox-tabs" role="tablist" aria-label="Inbox tabs">
              {CATEGORIES.map((entry) => {
                const unread = entry === "primary" ? 0 : (tabUnread?.[entry] ?? 0);
                return (
                  <button
                    key={entry}
                    type="button"
                    role="tab"
                    class={`inbox-tab${entry === tab ? " active" : ""}`}
                    aria-selected={entry === tab}
                    onClick={() => chooseTab(entry)}
                  >
                    <Icon name={TAB_ICONS[entry]} size={16} />
                    <span>{TAB_NAMES[entry]}</span>
                    {unread > 0 && (
                      <span class="tab-badge" title={`${unread} unread`}>
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <MessageList
            title={title}
            messages={messages}
            loading={loading}
            layout={layout}
            listRef={listRef}
            empty={
              <Empty
                icon={source.kind === "search" ? "search" : "inbox"}
                title={
                  source.kind === "search"
                    ? "No matches"
                    : tab && tab !== "primary"
                      ? `Nothing in ${TAB_NAMES[tab]}`
                      : "Nothing here"
                }
                {...(source.kind === "search"
                  ? { text: "Try fewer words, or operators like from:, subject:, has:attachment." }
                  : {})}
              />
            }
          >
            {messages.map((message, index) => (
              <AvatarLookup.Provider key={message.id} value={!junkFolders.has(message.folderId)}>
                <Row
                  message={message}
                  index={index}
                  active={openMessage?.id === message.id}
                  focused={keyboardFocus && index === focus}
                  checked={selected.has(message.id)}
                  showRecipient={role === "sent" || role === "drafts"}
                  labels={labels}
                  meId={me?.id ?? null}
                  compact={layout.density === "compact"}
                  listStyle={layout.listStyle}
                  archivable={role !== "archive"}
                  // Dropping on a folder moves within the mailbox on screen, so the unified
                  // inbox doesn't drag.
                  draggable={!unified}
                  mailboxLabel={unified ? mailboxLabel(message) : null}
                  handlers={handlersFor}
                />
              </AvatarLookup.Provider>
            ))}
            {data?.nextCursor && (
              <button type="button" class="load-more" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            )}
          </MessageList>
        </section>
      )}
      {showList && pane === "right" && <PaneDivider />}
      {showReader ? (
        <Reader
          key={open}
          openId={open ?? ""}
          conversation={conversation}
          folderId={folder?.id ?? null}
          onClose={() => navigate(sourcePath(source))}
          onRemoved={(ids) => afterRemoval(ids)}
        />
      ) : (
        pane !== "off" && (
          <section class="reader-pane empty-reader" data-part="reader">
            <Empty
              icon="mail"
              title="No conversation selected"
              text={`Pick a message ${pane === "bottom" ? "above" : "in the list"}. Press ? for keyboard shortcuts.`}
            />
          </section>
        )
      )}
    </div>
  );
};

/** The bar over the list: select all, the actions, the title and the count. */
const ListToolbar = overridable(
  "ListToolbar",
  ({ title, total, selected, allSelected, onToggleAll, children }) => (
    <div class="toolbar list-toolbar" data-part="list-toolbar">
      <label class="select-all-hit">
        {" "}
        <input
          type="checkbox"
          class="select-all"
          aria-label="Select all"
          checked={allSelected}
          indeterminate={selected > 0 && !allSelected}
          onChange={onToggleAll}
        />
      </label>
      {children}
      <span class="toolbar-title">{title}</span>
      {total !== null && <span class="toolbar-count">{total.toLocaleString()}</span>}
    </div>
  ),
);

/** The scrolling list: a spinner while loading, the empty state, or the rows. */
const MessageList = overridable(
  "MessageList",
  ({ messages, loading, listRef, empty, children }) => (
    <div class="message-list" data-part="message-list" ref={listRef} role="list">
      {loading && <Spinner />}
      {!loading && messages.length === 0 && empty}
      {children}
    </div>
  ),
);

const DefaultMessageRow = memo(function MessageRow({
  message,
  index,
  active,
  focused,
  checked,
  showRecipient,
  labels,
  meId,
  compact,
  archivable,
  draggable,
  mailboxLabel,
  handlers,
  badges,
}: MessageRowProps) {
  const act = () => handlers(message, index);
  const person = showRecipient
    ? (message.to[0] ?? { name: "(no recipient)", address: "" })
    : message.from;
  const people = showRecipient
    ? message.to.map(displayName).join(", ") || "(no recipient)"
    : displayName(message.from);
  const rowLabels = useMemo(
    () =>
      message.labelIds
        .map((id) => labels.find((label) => label.id === id))
        .filter((label) => label !== undefined),
    [message.labelIds, labels],
  );
  const swipe = useSwipe({
    onLeft: archivable ? () => act().archive() : undefined,
    onRight: () => act().toggleRead(),
  });
  return (
    <div
      role="listitem"
      data-part="message-row"
      data-read-label={message.seen ? "Unread" : "Read"}
      onPointerDown={swipe.onPointerDown}
      onPointerMove={swipe.onPointerMove}
      onPointerUp={swipe.onPointerUp}
      onPointerCancel={swipe.onPointerCancel}
      class={`message-row${message.seen ? "" : " unread"}${active ? " active" : ""}${focused ? " focused" : ""}${checked ? " checked" : ""}${compact ? " compact" : ""}`}
      draggable={draggable}
      onDragStart={(event) => {
        if (!draggable) return;
        event.dataTransfer?.setData("application/x-cloudmail-ids", JSON.stringify(act().dragIds()));
        if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
      }}
      onClick={(event) => {
        if (swipe.swallowClick()) return;
        if ((event.target as HTMLElement).closest("input, button")) return;
        act().onOpen();
      }}
      onContextMenu={(event) => act().onContextMenu(event)}
    >
      <div class="row-lead">
        {<Avatar person={person} aria-hidden="true" />}
        <input
          type="checkbox"
          class="row-check"
          aria-label="Select"
          checked={checked}
          onChange={(event) => act().onCheck((event.target as HTMLInputElement).checked)}
        />
      </div>
      <div class="row-main">
        <div class="row-top">
          <span class="row-from">
            {message.isDraft && <span class="draft-tag">Draft</span>}
            {people}
            {message.threadCount > 1 && <span class="thread-count">{message.threadCount}</span>}
          </span>
          <span class="row-icons">
            {message.vip && (
              <span class="row-vip" title="VIP">
                <Icon name="crown" size={13} />
              </span>
            )}
            {message.pinned && (
              <span class="row-pin" title="Pinned">
                <Icon name="pin" size={13} />
              </span>
            )}
            {message.muted && (
              <span title="Muted">
                <Icon name="bellOff" size={13} />
              </span>
            )}
            {message.answered && <Icon name="reply" size={13} />}
            {message.forwarded && <Icon name="forward" size={13} />}
            {message.hasAttachments && <Icon name="paperclip" size={14} />}
          </span>
          <span class="row-date" title={new Date(message.date).toLocaleString()}>
            {message.sendAt ? (
              <span class="scheduled">
                <Icon name="clock" size={12} /> {formatListDate(message.sendAt)}
              </span>
            ) : message.snoozedUntil ? (
              <span class="scheduled">
                <Icon name="alarm" size={12} /> {formatListDate(message.snoozedUntil)}
              </span>
            ) : (
              formatListDate(message.date)
            )}
          </span>
        </div>
        <div class="row-bottom">
          {message.nudgedAt && (
            <span class="nudge-chip" title="You sent this and nobody has replied yet">
              <Icon name="alarm" size={11} />
              No reply
            </span>
          )}
          <span class="row-subject">{message.subject || "(no subject)"}</span>
          {message.snippet && (
            <span class="row-snippet">{cleanSnippet(message.snippet, message.subject)}</span>
          )}
          {mailboxLabel && (
            <span class="mailbox-chip" title={`In ${mailboxLabel}`}>
              <Icon name="users" size={11} />
              {mailboxLabel}
            </span>
          )}
          {message.assignee && (
            <span class="assignee-chip" title={`Assigned to ${message.assignee.name}`}>
              <Icon name="user" size={11} />
              {message.assignee.id === meId ? "Me" : message.assignee.name}
            </span>
          )}
          {rowLabels.map((label) => (
            <span class="label-chip" key={label.id} style={{ "--chip": label.color }}>
              {label.name}
            </span>
          ))}
          {badges}
          <button
            type="button"
            class={`flag${message.flagged ? " on" : ""}`}
            aria-label={message.flagged ? "Unflag" : "Flag"}
            aria-pressed={message.flagged}
            onClick={() => act().onFlag()}
          >
            <Icon name="star" size={16} />
          </button>
        </div>
      </div>
      <div class="row-quick">
        {archivable && (
          <IconButton icon="archive" label="Archive" onClick={() => act().archive()} />
        )}
        <IconButton icon="trash" label="Delete" onClick={() => act().remove()} />
        <IconButton
          icon={message.seen ? "mail" : "mailOpen"}
          label={message.seen ? "Mark as unread" : "Mark as read"}
          onClick={() => act().toggleRead()}
        />
        {!message.isDraft && (
          <IconButton
            icon="alarm"
            label="Snooze"
            onClick={(event: MouseEvent) => act().snooze(event)}
          />
        )}
        {/* The strip covers the row's own flag button, so it carries one too. */}
        <IconButton
          icon="star"
          label={message.flagged ? "Unflag" : "Flag"}
          active={message.flagged}
          onClick={() => act().onFlag()}
        />
      </div>
    </div>
  );
});

const MessageRow = overridable("MessageRow", DefaultMessageRow);

/** What modules add to a row, such as an extension's badge. */
const RowBadges = ({ message, meId }: { message: MessageSummary; meId: string | null }) => {
  const badges = useListBadges();
  // The shared mailbox the row is in (in the unified inbox, its own), or null for the person's.
  const mailbox = message.mailboxId
    ? message.mailboxId === meId
      ? null
      : message.mailboxId
    : getCurrentMailbox();
  return (
    <>
      {badges.map((Badge, index) => (
        <Badge key={index} message={message} mailbox={mailbox} />
      ))}
    </>
  );
};

/** A row with the modules' badges, which a theme's MessageRow gets as `badges` too. */
const Row = memo((props: Omit<MessageRowProps, "badges">) => (
  <MessageRow {...props} badges={<RowBadges message={props.message} meId={props.meId} />} />
));
